import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommonPrescription,
  DocumentProps,
  PrescriptionQFHeaderMapper,
  PrescriptionStatus,
  PrescriptionTypeMapper,
  RequestType,
  PrescriptionPayload,
} from "../../interfaces";
import {
  FullScreenLoader,
  PrescriptionStatusChip,
  NavigateBeforeButton,
  FormHeader,
  FormContentContainer,
  FormBody,
} from "../../components";
import {
  GeneralInfoCard,
  HolderInformation,
  PrescriptionBottomActionArea,
  PrescriptionInfo,
  SignatureCard,
} from "./components";
import { usePrescription } from "./hooks/usePrescription";
import { useRequestSummary } from "../../hooks";
import {
  StyledDocument,
  StyledPage,
  WaterMarkStyle,
} from "../../components/styles";
import { DividerStyled } from "./styles";
import { CircularProgress } from "@mui/material";
import { generatePdfName } from "../../utils";
import Tracker from "@openreplay/tracker";
import { SHOULD_TRACK_SESSION, tracker } from "../../utils/openreplay";

export const PrescriptionValidationScreen: React.FC = () => {
  const { data, isFetching } = usePrescription();
  const navigate = useNavigate();
  const [numPages, setNumPages] = useState(1);

  const requestId = data?.prescription.requestId;
  const { data: requestSummary } = useRequestSummary(requestId);
  const handleOnLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
    },
    []
  );

  const handleBack = useCallback(() => {
    navigate("/app/farmacia");
  }, [navigate]);

  useEffect(() => {
    if (!data && !isFetching) {
      handleBack();
    }
  }, [data, isFetching, handleBack]);

  const hasOnlySimplePrescriptions = useMemo(() => {
    if (!data) {
      return false;
    }
    const isCompoundedPrescription =
      data.prescription.type === RequestType.COMPOUNDED_PRESCRIPTION;
    if (isCompoundedPrescription) {
      return false;
    }
    if (
      !requestSummary ||
      requestSummary.documentType !== RequestType.PRESCRIPTION
    ) {
      return false;
    }
    const prescriptionPayload = requestSummary as PrescriptionPayload;
    const prescriptions = prescriptionPayload.documents || [];
    return (
      prescriptions.length > 0 &&
      prescriptions.every((prescription) => prescription.type === "SIMPLE")
    );
  }, [data, requestSummary]);

  const signatureType = hasOnlySimplePrescriptions ? "FES" : "FEA";

  if (isFetching)
    return (
      <>
        <FormHeader
          title={""}
          titleColor="white"
          left={<NavigateBeforeButton iconColor="white" onClick={handleBack} />}
        />
        <FormContentContainer flex={1} disableGutters>
          <FormBody>
            <FullScreenLoader title="Cargando información..." />
          </FormBody>
        </FormContentContainer>
      </>
    );

  if (!data) {
    return null;
  }

  const {
    prescription,
    prescriptionPDFReadUrl,
    doctor,
    patient,
    treatmentPDFReadUrl,
  } = data;

  const isCompoundedPrescription =
    prescription.type === RequestType.COMPOUNDED_PRESCRIPTION;

  const prescriptionPDFName = generatePdfName({
    patient,
    documentType: isCompoundedPrescription
      ? RequestType.COMPOUNDED_PRESCRIPTION
      : RequestType.PRESCRIPTION,
    ...(!isCompoundedPrescription && {
      documentNameDetails: PrescriptionTypeMapper[prescription.type],
    }),
  });
  const prescriptionPDF: DocumentProps = {
    documentId: prescription.id,
    documentUrl: prescriptionPDFReadUrl,
    documentName: prescriptionPDFName,
  };
  const reducedPrescriptionData = {
    id: prescription.id,
    requestId: prescription.requestId,
    type: prescription.type,
    prescriptionStatus: prescription.prescriptionStatus,
    details: prescription.details,
    holdingDate: prescription.holdingDate,
  };
  const prescriptionPDFData = { prescriptionPDFReadUrl, prescriptionPDFName };

  const treatmentPDF: DocumentProps = treatmentPDFReadUrl
    ? {
        documentId: prescription.requestId,
        documentUrl: treatmentPDFReadUrl,
        documentName: generatePdfName({
          patient,
          documentType: RequestType.PRESCRIPTION,
        }),
      }
    : ({
        documentId: prescription.requestId,
        documentUrl: "",
        documentName: "",
      } as DocumentProps);

  if (SHOULD_TRACK_SESSION && tracker) {
    identifyOpenReplaySession(prescription, tracker);
  }
  const showDocumentInformationSeparator =
    prescription.prescriptionStatus === PrescriptionStatus.ISSUED;

  return (
    <>
      <FormHeader
        title={PrescriptionQFHeaderMapper[prescription.prescriptionStatus]}
        titleColor="white"
        left={<NavigateBeforeButton iconColor="white" onClick={handleBack} />}
      />
      <FormContentContainer disableGutters>
        <FormBody>
          <PrescriptionStatusChip
            code={prescription.id}
            status={prescription.prescriptionStatus}
            date={prescription.holdingDate}
            isCompoundedPrescription={isCompoundedPrescription}
            type={prescription.type}
          />

          {prescription.holding?.pharmaceuticalChemist && (
            <>
              <HolderInformation
                prescriptionHolding={prescription.holding}
                holderData={prescription.holding.pharmaceuticalChemist}
                status={prescription.prescriptionStatus}
                isCompoundedPrescription={isCompoundedPrescription}
              />
              <DividerStyled />
            </>
          )}
          <GeneralInfoCard
            emissionDate={new Date(prescription.createdAt)}
            doctorData={doctor}
            patientData={patient}
            showTitle={!showDocumentInformationSeparator}
          />
          {prescription.prescriptionStatus !== PrescriptionStatus.CANCELED && (
            <>
              {showDocumentInformationSeparator && <DividerStyled />}
              <PrescriptionInfo
                prescriptionData={reducedPrescriptionData}
                prescriptionPDF={prescriptionPDF}
              />
              <DividerStyled />
              <SignatureCard
                doctorData={doctor}
                treatmentPDF={treatmentPDF}
                signatureType={signatureType}
              />
              <PrescriptionBottomActionArea
                prescription={prescription}
                prescriptionPDFData={prescriptionPDFData}
              />
            </>
          )}
          {prescription.prescriptionStatus === PrescriptionStatus.CANCELED && (
            <StyledDocument
              file={prescriptionPDFReadUrl}
              loading={<CircularProgress size={24} color="primary" />}
              onLoadSuccess={handleOnLoadSuccess}
              blur
            >
              {numPages &&
                Array.from(new Array(numPages), (_, pageIndex) => (
                  <React.Fragment key={`page_${pageIndex}`}>
                    <StyledPage
                      pageNumber={pageIndex + 1}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    >
                      <WaterMarkStyle>ANULADA</WaterMarkStyle>
                    </StyledPage>
                  </React.Fragment>
                ))}
            </StyledDocument>
          )}
        </FormBody>
      </FormContentContainer>
    </>
  );
};

function identifyOpenReplaySession(
  prescription: CommonPrescription,
  tracker: Tracker | null
) {
  if (!tracker) return;
  if (prescription.holding && prescription.holding.pharmaceuticalChemist) {
    const {
      holding,
      holding: { pharmaceuticalChemist },
    } = prescription;

    tracker.setUserID(pharmaceuticalChemist.rut);
    tracker.setMetadata(
      "name",
      `${pharmaceuticalChemist.name} ${pharmaceuticalChemist.lastName}`
    );
    tracker.setMetadata("email", pharmaceuticalChemist.email);
    tracker.setMetadata("code", pharmaceuticalChemist.code);

    tracker.setMetadata("pharmacyName", holding.pharmacyName);
    tracker.setMetadata("pharmacyRut", holding.pharmacyRut);
  } else {
    tracker.setUserID("Sesión de farmacia");
    tracker.setMetadata("name", "Sesión de farmacia");
  }
}
